import React from "react";
import { Box, ButtonBase } from "@mui/material";
import { useBoundStore } from "../../store";
import { ThemeMode } from "../../store/theme";

// Segmented sun / moon theme switch (spec §06): a --surface-2 trough with
// 32×30 segments; the selected segment lifts to --surface + --sh-1 + --text.
// Two-state by design (no auto/system yet). Inline stroke SVGs — the spec
// forbids unicode glyphs/emoji for icons.

const stroke = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const
};

function SunIcon() {
    return (
        <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden focusable="false">
            <circle cx="12" cy="12" r="4" {...stroke} />
            <path
                d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
                {...stroke}
            />
        </svg>
    );
}

function MoonIcon() {
    return (
        <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden focusable="false">
            <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" {...stroke} />
        </svg>
    );
}

interface SegmentProps {
    target: ThemeMode;
    label: string;
    children: React.ReactNode;
}

function Segment({ target, label, children }: SegmentProps) {
    const mode = useBoundStore((s) => s.themeMode);
    const setThemeMode = useBoundStore((s) => s.setThemeMode);
    const selected = mode === target;

    return (
        <ButtonBase
            disableRipple
            aria-label={label}
            aria-pressed={selected}
            onClick={() => setThemeMode(target)}
            sx={{
                width: 32,
                height: 30,
                borderRadius: "6px",
                color: selected ? "var(--text)" : "var(--text-2)",
                bgcolor: selected ? "var(--surface)" : "transparent",
                boxShadow: selected ? "var(--sh-1)" : "none",
                transition:
                    "background-color 140ms cubic-bezier(.2,.7,.2,1), color 140ms cubic-bezier(.2,.7,.2,1), box-shadow 140ms cubic-bezier(.2,.7,.2,1)",
                "&:hover": { color: "var(--text)" }
            }}
        >
            {children}
        </ButtonBase>
    );
}

export function ThemeToggle() {
    return (
        <Box
            role="group"
            aria-label="Color theme"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "2px",
                p: "3px",
                borderRadius: "8px",
                bgcolor: "var(--surface-2)"
            }}
        >
            <Segment target="light" label="Light theme">
                <SunIcon />
            </Segment>
            <Segment target="dark" label="Dark theme">
                <MoonIcon />
            </Segment>
        </Box>
    );
}
